import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plug, Loader2, RefreshCw, CheckCircle2, Link2, Unlink, CreditCard, BarChart3, Building2, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDateTime, formatCurrency } from "@/lib/format";

const ICONS = { stripe: CreditCard, quickbooks: Building2, google_analytics: BarChart3, hubspot: BarChart3 };

export default function Integrations() {
  const { company } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try { const { data } = await api.get(`/companies/${company.id}/integrations`); setItems(data); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const connect = async () => {
    if (!key.trim()) return toast.error("Paste your Stripe key");
    setConnecting(true);
    try {
      await api.post(`/companies/${company.id}/integrations/stripe/connect`, { stripe_key: key });
      toast.success("Stripe connected");
      setOpen(false); setKey(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not connect"); }
    finally { setConnecting(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/integrations/stripe/sync`);
      toast.success(`Synced: MRR ${formatCurrency(data.mrr)}, ${data.customers} customers (${data.period})`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const disconnect = async () => {
    try { await api.delete(`/companies/${company.id}/integrations/stripe`); toast.success("Disconnected"); load(); }
    catch { toast.error("Failed"); }
  };

  const stripe = items.find((i) => i.provider === "stripe");

  return (
    <AppShell title="Integrations" subtitle="Auto-pull metrics from the tools you already use">
        <div data-testid="integrations-notice" className="mb-6 flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/10 px-4 py-3 text-foreground">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="text-sm">Connect a data source and we'll auto-fill your metrics for the current month. Stripe is live now; more providers are on the way.</div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((it) => {
            const Icon = ICONS[it.provider] || Plug;
            const connected = it.status === "connected";
            const soon = it.status === "coming_soon";
            return (
              <Card key={it.provider} className="rounded-xl border bg-card p-5" data-testid={`integration-${it.provider}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
                    <div>
                      <div className="h-display text-base font-semibold text-foreground">{it.name}</div>
                      {connected ? <Badge className="mt-0.5 gap-1 border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" />Connected · {it.mode}</Badge>
                        : soon ? <Badge variant="secondary" className="mt-0.5 font-normal">Coming soon</Badge>
                        : <Badge variant="outline" className="mt-0.5 font-normal">Not connected</Badge>}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{it.description}</p>

                {it.provider === "stripe" && connected && it.last_result && (
                  <div className="mt-3 rounded-lg bg-secondary/60 p-3 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Last sync MRR</span><span className="h-display font-semibold">{formatCurrency(it.last_result.mrr)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Customers</span><span className="h-display font-semibold">{it.last_result.customers}</span></div>
                    <div className="mt-1 text-xs text-muted-foreground">{it.last_synced_at ? `Synced ${formatDateTime(it.last_synced_at)}` : ""}</div>
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  {it.provider === "stripe" ? (
                    connected ? (
                      <>
                        <Button onClick={sync} disabled={syncing} className="gap-2" data-testid="stripe-sync">{syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now</Button>
                        <Button variant="outline" onClick={disconnect} className="gap-2" data-testid="stripe-disconnect"><Unlink className="h-4 w-4" /> Disconnect</Button>
                      </>
                    ) : (
                      <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><Button className="gap-2" data-testid="stripe-connect-open"><Link2 className="h-4 w-4" /> Connect Stripe</Button></DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Connect Stripe</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">Create a <b>restricted key</b> in Stripe (Developers -> API keys) with read access to Customers, Subscriptions and Prices. Paste it below - it's encrypted and never shown again.</p>
                            <div>
                              <Label htmlFor="sk">Stripe restricted secret key</Label>
                              <Input id="sk" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="rk_live_... or rk_test_..." className="mt-1" data-testid="stripe-key-input" />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button onClick={connect} disabled={connecting} className="gap-2" data-testid="stripe-connect-confirm">{connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Connect</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )
                  ) : (
                    <Button variant="outline" disabled className="gap-2">Coming soon</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
